// Procedural story music with suspense/adventure theme
// Atmospheric and cinematic for visual novel narrative sections

interface StoryMusicState {
  audioContext: AudioContext | null;
  masterGain: GainNode | null;
  oscillators: OscillatorNode[];
  gains: GainNode[];
  lfoNodes: OscillatorNode[];
  isPlaying: boolean;
  intervalId: ReturnType<typeof setInterval> | null;
  currentStep: number;
  intensity: number; // 0 = calm, 1 = intense
}

let storyMusicState: StoryMusicState = {
  audioContext: null,
  masterGain: null,
  oscillators: [],
  gains: [],
  lfoNodes: [],
  isPlaying: false,
  intervalId: null,
  currentStep: 0,
  intensity: 0.3,
};

function createNoiseBuffer(ctx: AudioContext, duration: number): AudioBuffer {
  const bufferSize = ctx.sampleRate * duration;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  return buffer;
}

export function createStoryMusic(): {
  play: () => void;
  stop: () => void;
  volume: (v: number) => void;
  setIntensity: (i: number) => void;
  playing: () => boolean;
} | null {
  if (typeof window === "undefined") return null;

  const bpm = 60; // Slow for atmosphere
  const stepDuration = (60 / bpm) / 4;

  // Cinematic chord progression: minor, suspended, diminished
  // Am - Asus2 - Bdim - Am
  const baseFreq = 110; // A2
  const chordProgression = [
    { root: 1.0, third: 1.2, fifth: 1.5 },      // Am
    { root: 1.0, third: 1.125, fifth: 1.5 },    // Asus2
    { root: 1.125, third: 1.333, fifth: 1.6 },  // Bdim
    { root: 1.0, third: 1.2, fifth: 1.5 },      // Am
  ];

  const getChord = (step: number) => {
    const bar = Math.floor(step / 32) % 4;
    return chordProgression[bar];
  };

  // Patterns for different layers
  const padPattern = [1, 1, 1, 1, 1, 1, 1, 1]; // Sustained
  const pulsePattern = [0.8, 0, 0.4, 0, 0.8, 0, 0.4, 0, 0.8, 0, 0.6, 0, 0.8, 0, 0.4, 0];
  const bassPattern = [1, 0, 0, 0, 0, 0, 0, 0, 0.8, 0, 0, 0, 0, 0, 0, 0];
  const tensionPattern = [0, 0, 0, 0, 0, 0, 0, 0.3, 0, 0, 0, 0, 0, 0.4, 0.5, 0.6];

  let targetVolume = 0.4;

  const theme = {
    play: () => {
      if (storyMusicState.isPlaying) return;

      try {
        if (!storyMusicState.audioContext || storyMusicState.audioContext.state === "closed") {
          storyMusicState.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        }

        const ctx = storyMusicState.audioContext;

        if (ctx.state === "suspended") {
          ctx.resume();
        }

        // Master gain with compressor for cinematic sound
        storyMusicState.masterGain = ctx.createGain();
        storyMusicState.masterGain.gain.value = targetVolume;

        const compressor = ctx.createDynamicsCompressor();
        compressor.threshold.value = -24;
        compressor.knee.value = 30;
        compressor.ratio.value = 4;
        compressor.attack.value = 0.003;
        compressor.release.value = 0.25;

        storyMusicState.masterGain.connect(compressor);
        compressor.connect(ctx.destination);

        storyMusicState.isPlaying = true;
        storyMusicState.currentStep = 0;

        // Create pad oscillators (warm, sustained)
        const padOsc = ctx.createOscillator();
        const padGain = ctx.createGain();
        const padFilter = ctx.createBiquadFilter();
        padOsc.type = "sine";
        padOsc.frequency.value = baseFreq;
        padFilter.type = "lowpass";
        padFilter.frequency.value = 800;
        padFilter.Q.value = 1;
        padGain.gain.value = 0;
        padOsc.connect(padFilter);
        padFilter.connect(padGain);
        padGain.connect(storyMusicState.masterGain);
        padOsc.start();
        storyMusicState.oscillators.push(padOsc);
        storyMusicState.gains.push(padGain);

        // Pad third
        const padOsc2 = ctx.createOscillator();
        const padGain2 = ctx.createGain();
        padOsc2.type = "sine";
        padOsc2.frequency.value = baseFreq * 1.2;
        padGain2.gain.value = 0;
        padOsc2.connect(padGain2);
        padGain2.connect(storyMusicState.masterGain);
        padOsc2.start();
        storyMusicState.oscillators.push(padOsc2);
        storyMusicState.gains.push(padGain2);

        // Pad fifth
        const padOsc3 = ctx.createOscillator();
        const padGain3 = ctx.createGain();
        padOsc3.type = "sine";
        padOsc3.frequency.value = baseFreq * 1.5;
        padGain3.gain.value = 0;
        padOsc3.connect(padGain3);
        padGain3.connect(storyMusicState.masterGain);
        padOsc3.start();
        storyMusicState.oscillators.push(padOsc3);
        storyMusicState.gains.push(padGain3);

        // Deep bass
        const bassOsc = ctx.createOscillator();
        const bassGain = ctx.createGain();
        bassOsc.type = "sine";
        bassOsc.frequency.value = baseFreq * 0.5;
        bassGain.gain.value = 0;
        bassOsc.connect(bassGain);
        bassGain.connect(storyMusicState.masterGain);
        bassOsc.start();
        storyMusicState.oscillators.push(bassOsc);
        storyMusicState.gains.push(bassGain);

        // Tension oscillator (high, eerie)
        const tensionOsc = ctx.createOscillator();
        const tensionGain = ctx.createGain();
        const tensionFilter = ctx.createBiquadFilter();
        tensionOsc.type = "triangle";
        tensionOsc.frequency.value = baseFreq * 4;
        tensionFilter.type = "highpass";
        tensionFilter.frequency.value = 1000;
        tensionGain.gain.value = 0;
        tensionOsc.connect(tensionFilter);
        tensionFilter.connect(tensionGain);
        tensionGain.connect(storyMusicState.masterGain);
        tensionOsc.start();
        storyMusicState.oscillators.push(tensionOsc);
        storyMusicState.gains.push(tensionGain);

        // Pulse/arpeggio oscillator
        const pulseOsc = ctx.createOscillator();
        const pulseGain = ctx.createGain();
        pulseOsc.type = "triangle";
        pulseOsc.frequency.value = baseFreq * 2;
        pulseGain.gain.value = 0;
        pulseOsc.connect(pulseGain);
        pulseGain.connect(storyMusicState.masterGain);
        pulseOsc.start();
        storyMusicState.oscillators.push(pulseOsc);
        storyMusicState.gains.push(pulseGain);

        // LFO for subtle vibrato
        const lfo = ctx.createOscillator();
        const lfoGain = ctx.createGain();
        lfo.type = "sine";
        lfo.frequency.value = 0.5; // Very slow
        lfoGain.gain.value = 2; // Subtle pitch variation
        lfo.connect(lfoGain);
        lfoGain.connect(padOsc.frequency);
        lfoGain.connect(padOsc2.frequency);
        lfoGain.connect(padOsc3.frequency);
        lfo.start();
        storyMusicState.lfoNodes.push(lfo);

        // Noise buffer for atmosphere
        const noiseBuffer = createNoiseBuffer(ctx, 4);

        storyMusicState.intervalId = setInterval(() => {
          if (!storyMusicState.isPlaying || !storyMusicState.audioContext) return;

          const step = storyMusicState.currentStep;
          const ctx = storyMusicState.audioContext;
          const now = ctx.currentTime;
          const chord = getChord(step);
          const intensity = storyMusicState.intensity;

          // Update pad frequencies based on chord
          const [padOsc, padOsc2, padOsc3, bassOsc, tensionOsc, pulseOsc] = storyMusicState.oscillators;
          const [padGain, padGain2, padGain3, bassGain, tensionGain, pulseGain] = storyMusicState.gains;

          // Smooth frequency transitions
          padOsc?.frequency.setTargetAtTime(baseFreq * chord.root, now, 0.5);
          padOsc2?.frequency.setTargetAtTime(baseFreq * chord.third, now, 0.5);
          padOsc3?.frequency.setTargetAtTime(baseFreq * chord.fifth, now, 0.5);
          bassOsc?.frequency.setTargetAtTime(baseFreq * chord.root * 0.5, now, 0.3);

          // Pad layer (always present, volume based on intensity)
          const padVol = 0.12 + intensity * 0.08;
          padGain?.gain.setTargetAtTime(padVol * 0.4, now, 0.3);
          padGain2?.gain.setTargetAtTime(padVol * 0.3, now, 0.3);
          padGain3?.gain.setTargetAtTime(padVol * 0.25, now, 0.3);

          // Bass pattern
          const bassValue = bassPattern[step % bassPattern.length] || 0;
          if (bassValue > 0) {
            bassGain?.gain.setValueAtTime(0.2 * bassValue * (0.5 + intensity * 0.5), now);
            bassGain?.gain.exponentialRampToValueAtTime(0.001, now + stepDuration * 2);
          }

          // Tension layer (increases with intensity)
          const tensionValue = tensionPattern[step % tensionPattern.length] || 0;
          if (tensionValue > 0 && intensity > 0.4) {
            const tensionVol = tensionValue * (intensity - 0.4) * 0.15;
            tensionGain?.gain.setValueAtTime(tensionVol, now);
            tensionGain?.gain.exponentialRampToValueAtTime(0.001, now + stepDuration * 0.8);
          }

          // Pulse/arpeggio (subtle movement)
          const pulseValue = pulsePattern[step % pulsePattern.length] || 0;
          if (pulseValue > 0) {
            // Arpeggiate through chord notes
            const arpNote = [chord.root, chord.third, chord.fifth][(step % 3)];
            pulseOsc?.frequency.setValueAtTime(baseFreq * 2 * arpNote, now);
            pulseGain?.gain.setValueAtTime(pulseValue * 0.06 * (0.3 + intensity * 0.7), now);
            pulseGain?.gain.exponentialRampToValueAtTime(0.001, now + stepDuration * 0.6);
          }

          // Atmospheric noise (sparse, filtered)
          if (step % 16 === 0 && intensity > 0.5) {
            const noise = ctx.createBufferSource();
            noise.buffer = noiseBuffer;
            const noiseGain = ctx.createGain();
            const noiseFilter = ctx.createBiquadFilter();
            noiseFilter.type = "bandpass";
            noiseFilter.frequency.value = 1500 + Math.random() * 500;
            noiseFilter.Q.value = 5;
            noise.connect(noiseFilter);
            noiseFilter.connect(noiseGain);
            noiseGain.connect(storyMusicState.masterGain!);
            noiseGain.gain.setValueAtTime(0.02 * intensity, now);
            noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 1);
            noise.start(now);
            noise.stop(now + 1.5);
          }

          storyMusicState.currentStep++;
        }, stepDuration * 1000);

        console.log("[StoryMusic] Started playing");
      } catch (error) {
        console.error("[StoryMusic] Error starting:", error);
      }
    },

    stop: () => {
      if (!storyMusicState.isPlaying) return;

      storyMusicState.isPlaying = false;

      if (storyMusicState.intervalId) {
        clearInterval(storyMusicState.intervalId);
        storyMusicState.intervalId = null;
      }

      storyMusicState.oscillators.forEach((osc) => {
        try {
          osc.stop();
          osc.disconnect();
        } catch (e) {}
      });
      storyMusicState.oscillators = [];

      storyMusicState.lfoNodes.forEach((osc) => {
        try {
          osc.stop();
          osc.disconnect();
        } catch (e) {}
      });
      storyMusicState.lfoNodes = [];

      storyMusicState.gains.forEach((gain) => {
        try {
          gain.disconnect();
        } catch (e) {}
      });
      storyMusicState.gains = [];

      if (storyMusicState.masterGain) {
        storyMusicState.masterGain.disconnect();
        storyMusicState.masterGain = null;
      }

      console.log("[StoryMusic] Stopped");
    },

    volume: (v: number) => {
      targetVolume = Math.max(0, Math.min(1, v));
      if (storyMusicState.masterGain) {
        storyMusicState.masterGain.gain.value = targetVolume;
      }
    },

    setIntensity: (i: number) => {
      storyMusicState.intensity = Math.max(0, Math.min(1, i));
    },

    playing: () => storyMusicState.isPlaying,
  };

  return theme;
}

export function fadeOutStoryMusic(theme: ReturnType<typeof createStoryMusic>, duration: number = 1500) {
  if (!theme || !theme.playing()) return;

  const steps = 30;
  const stepTime = duration / steps;
  let currentStep = 0;
  let currentVolume = 0.4;

  const interval = setInterval(() => {
    currentStep++;
    const progress = currentStep / steps;
    const newVolume = currentVolume * (1 - progress);
    theme.volume(newVolume);

    if (currentStep >= steps) {
      clearInterval(interval);
      theme.stop();
    }
  }, stepTime);
}

export function fadeInStoryMusic(theme: ReturnType<typeof createStoryMusic>, targetVolume: number = 0.4, duration: number = 2000) {
  if (!theme) return;

  theme.volume(0);
  theme.play();

  const steps = 40;
  const stepTime = duration / steps;
  let currentStep = 0;

  const interval = setInterval(() => {
    currentStep++;
    const progress = currentStep / steps;
    // Ease in curve for smoother fade
    const eased = progress * progress;
    const newVolume = targetVolume * eased;
    theme.volume(newVolume);

    if (currentStep >= steps) {
      clearInterval(interval);
    }
  }, stepTime);
}
